// ─────────────────────────────────────────────────────────────────────────────
// AboutPage, the company page.
//
// This page argues a category, not a biography. The back office sits between
// three systems that each stop short of it: the register stops at the receipt,
// the processor stops at the payout, the accountant starts a month later. That
// gap is the product, and it is what the page opens with.
//
// It deliberately names no customer, no founder and no shop. Wavelength is a
// software company; tying it to any single retail business is the exact
// confusion this rebuild exists to remove. What is left is the category, the
// beliefs the product is built on, how the team works, and an honest list of
// the things this software is not.
//
// Layout follows the shared kit: left-aligned headings that end in a period,
// alternating white and grey sections, one dark panel, and grey captioned
// <MediaSlot> stand-ins wherever photography does not exist yet.
// ─────────────────────────────────────────────────────────────────────────────
import { Link } from 'react-router-dom';
import {
    BTN_PRIMARY,
    BTN_SECONDARY,
    Icon,
    Container,
    Section,
    Heading,
    Lead,
    Body,
    Key,
    MediaSlot,
    SectionHead,
    FeatureRow,
    FeatureGrid,
    SpecList,
    PageHero,
    CTASection,
    MarketingShell,
    Seo,
} from '../../components/marketing/MarketingKit';
import { Reveal } from '../../components/marketing/Motion';

// ── The gap the company exists to fill ───────────────────────────────────────
const GAP = [
    {
        t: 'The register stops at the receipt',
        b: 'It records the sale and prints the total. It is never asked whether the money that sale represents actually arrived anywhere.',
    },
    {
        t: 'The processor stops at the payout',
        b: 'Card takings come back days later as one net line, fees already removed, often covering more than one store at once.',
    },
    {
        t: 'The accountant starts a month later',
        b: 'By then the day is a memory, the person who counted the drawer has moved on, and a difference small enough to ignore gets ignored.',
    },
];

// ── What the product is built on ─────────────────────────────────────────────
const BELIEFS = [
    {
        icon: 'wallet',
        title: 'Money needs a chain of custody',
        body: 'A dollar should be traceable from the drawer to the drop, the safe, the deposit and the bank line. A total with no trail behind it is a guess with good posture.',
    },
    {
        icon: 'lock',
        title: 'Ledgers are append-only',
        body: 'Nothing is overwritten. A correction is another entry, dated and attributed, so the record can always be read back the way it actually happened.',
    },
    {
        icon: 'alert',
        title: 'Software should say when it does not know',
        body: 'A screen that quietly fills a gap teaches people to trust it wrongly. When a source did not answer, we show that it did not answer instead of a confident zero.',
    },
    {
        icon: 'users',
        title: 'The floor makes the data',
        body: 'The numbers are made by the person counting the drawer and the person weighing the pack. If the tools in their hands are bad, nothing downstream can be good.',
    },
    {
        icon: 'shield',
        title: 'Boring where it counts',
        body: 'Roles, approvals and audit trails are not upsells. They are the reason a manager can be given access to anything at all.',
    },
    {
        icon: 'scale',
        title: 'Stay in one lane',
        body: 'We are not the register and not the general ledger. Doing one layer properly is worth more to an operator than doing three of them approximately.',
    },
];

// ── How the team operates, shown on the dark panel ───────────────────────────
const PRACTICE = [
    {
        t: 'Build next to the counter',
        b: 'Work starts from a real trading day: a close that ran long, a deposit that did not match, a count that nobody could explain.',
    },
    {
        t: 'Ship small and often',
        b: 'Changes go out in small pieces most weeks. A release that lands quietly and works is worth more than one that is announced.',
    },
    {
        t: 'Prove it against a real day',
        b: 'A change is finished when a closed day still ties out afterwards, not when the screen looks right.',
    },
    {
        t: 'Write down what we will not do',
        b: 'The roadmap is as much a list of refusals as a list of promises. Saying no early is cheaper for everyone than saying it late.',
    },
];

// ── The honest boundaries of the product ─────────────────────────────────────
const NOT = [
    {
        t: 'Not a point of sale',
        b: 'Keep the register you already run. Wavelength reads from it and adds the close, the cash controls and the reconciliation on top.',
    },
    {
        t: 'Not your accounting system',
        b: 'The closed day leaves as a journal entry your bookkeeper imports. We have no interest in becoming the general ledger.',
    },
    {
        t: 'Not a payments company',
        b: 'We never hold or move your money. We account for what the processor and the bank did with it, and we say when the two disagree.',
    },
    {
        t: 'Not a profit model',
        b: 'We do not track cost of goods or margin, so we will not tell you what you earned on a case. We tell you what came in and where it went.',
    },
];

const BRIEF = [
    { label: 'What it is', value: 'Back office and reconciliation software' },
    { label: 'Who it is for', value: 'Operators running more than one location' },
    { label: 'Where it runs', value: 'In a browser, and on iPad on the floor' },
    { label: 'Companion apps', value: 'Register, scale, receiving, packing, delivery' },
    { label: 'Hosted on', value: 'Amazon Web Services' },
    { label: 'What it costs', value: 'Published per location on the pricing page' },
    { label: 'Who owns the data', value: 'You, exportable from every table' },
];

export default function AboutPage() {
    return (
        <MarketingShell>
            <Seo
                title="About Wavelength, the back office for multi-location operators"
                description="Wavelength builds back office and reconciliation software for multi-location retail and food operators. What we believe, how we work, and what we do not do."
                path="/about"
                keywords="about wavelength, back office software company, retail reconciliation software, multi location operators, daily close software, cash control software, append only ledger"
                jsonLd={{
                    '@context': 'https://schema.org',
                    '@graph': [
                        {
                            '@type': 'AboutPage',
                            '@id': 'https://wavelengtherp.com/about#page',
                            url: 'https://wavelengtherp.com/about',
                            name: 'About Wavelength',
                            description:
                                'Wavelength builds back office and reconciliation software for multi-location retail and food operators.',
                            about: { '@id': 'https://wavelengtherp.com/#organization' },
                            publisher: { '@id': 'https://wavelengtherp.com/#organization' },
                        },
                        {
                            '@type': 'BreadcrumbList',
                            itemListElement: [
                                { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://wavelengtherp.com/' },
                                { '@type': 'ListItem', position: 2, name: 'About', item: 'https://wavelengtherp.com/about' },
                            ],
                        },
                    ],
                }}
            />

            {/* ── Hero ───────────────────────────────────────────────────── */}
            <PageHero
                eyebrow="About Wavelength"
                title="We build the back office nobody else owns."
                lead={<>Every operator has a register that records the sale, a processor that pays out and a bank that receives. Nobody owns the ground between them, so it gets done by hand every night. <Key>That ground is the product.</Key></>}
                primary={{ label: 'Book a walkthrough', to: '/contact' }}
                secondary={{ label: 'See the platform', to: '/features' }}
            >
                <MediaSlot
                    kind="Photo"
                    label="A back office after hours, one lamp on, the day's paperwork still on the desk"
                    note="Wide, low light, no people in frame. The laptop screen is the only bright thing in the picture."
                    ratio="16 / 9"
                    className="!rounded-[28px]"
                />
            </PageHero>

            {/* ── The unowned middle ─────────────────────────────────────── */}
            <Section tone="paper2" pad="lg">
                <Container>
                    <Reveal>
                        <SectionHead
                            eyebrow="The category"
                            title="The unowned middle."
                            lead="Three systems touch every dollar a store takes. Each of them stops just short of the question an owner actually has, which is whether the money got where it was supposed to go."
                            maxWidth="max-w-[560px]"
                        />
                    </Reveal>
                    <Reveal delay={80}>
                        <div className="mt-12 grid grid-cols-1 overflow-hidden rounded-[24px] border border-[rgba(10,10,10,0.10)] bg-white sm:grid-cols-3">
                            {GAP.map((x, i) => (
                                <div
                                    key={x.t}
                                    className={`flex flex-col gap-3 p-7 ${i > 0 ? 'border-t border-[rgba(10,10,10,0.08)] sm:border-l sm:border-t-0' : ''}`}
                                >
                                    <span className="text-[13px] font-medium tabular-nums text-[#A3A3A3]">{String(i + 1).padStart(2, '0')}</span>
                                    <div className="text-[17px] font-semibold tracking-[-0.015em] text-[#0A0A0A]">{x.t}</div>
                                    <Body>{x.b}</Body>
                                </div>
                            ))}
                        </div>
                    </Reveal>
                    <Reveal delay={140}>
                        <Lead className="mt-8 max-w-[620px]">
                            Between those three sits a nightly job that almost every multi-location operator still does on paper. Wavelength is a company built to do that job properly, and to do nothing else instead of it.
                        </Lead>
                    </Reveal>
                </Container>
            </Section>

            {/* ── Reconciliation as the product ──────────────────────────── */}
            <Section pad="lg">
                <Container>
                    <Reveal>
                        <FeatureRow
                            eyebrow="What we work on"
                            title="Reconciliation is a product, not a chore."
                            body="Treated as a chore, it is a spreadsheet somebody maintains until they leave. Treated as a product, it is a system that runs every night and hands you a short list in the morning."
                            bullets={[
                                'Register takings, processor batches and imported bank statements matched against each other',
                                'Everything that did not tie in one queue, with the location, the source, the date and the amount',
                                'Blind counts, so a variance is discovered rather than confirmed',
                                'A day that locks on sign-off, with an audit entry for every change made after it',
                            ]}
                            cta={<Link to="/how-it-works" className={BTN_SECONDARY}>See how it works<Icon name="arrow" className="h-4 w-4" /></Link>}
                            media={
                                <MediaSlot
                                    kind="Screenshot"
                                    label="Reconciliation screen with one unmatched deposit selected"
                                    note="Laptop screenshot: register takings, processor payouts and bank deposits in three columns, the odd row picked out"
                                    ratio="4 / 3"
                                    className="!rounded-[28px]"
                                />
                            }
                        />
                    </Reveal>
                </Container>
            </Section>

            {/* ── What we believe ────────────────────────────────────────── */}
            <Section tone="paper2" pad="lg">
                <Container>
                    <Reveal>
                        <SectionHead
                            eyebrow="What we believe"
                            title="Six positions we build from."
                            lead="These are not values on a wall. Each one decides how a screen behaves when the data is late, wrong or missing, which is most of the interesting cases."
                            maxWidth="max-w-[540px]"
                        />
                    </Reveal>
                    <Reveal delay={80}>
                        <FeatureGrid className="mt-12" items={BELIEFS} cols={3} />
                    </Reveal>
                </Container>
            </Section>

            {/* ── How we work ────────────────────────────────────────────── */}
            <Section pad="sm">
                <Container>
                    <Reveal>
                        <div className="relative overflow-hidden rounded-[28px] bg-[#111111] p-6 text-white sm:p-12 lg:p-14">
                            <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1fr_320px] lg:gap-14">
                                <div className="flex flex-col">
                                    <h2 className="max-w-[420px] text-[28px] font-semibold leading-[1.08] tracking-[-0.025em] text-white sm:text-[36px]">
                                        How we work.
                                    </h2>
                                    <p className="mt-4 max-w-[440px] text-[15px] leading-[1.65] text-white/60">
                                        A small team, close to the counter, shipping in small pieces. The work is judged by whether a real day ties out, not by how the release notes read.
                                    </p>
                                    <div className="mt-9 overflow-hidden rounded-[24px] border border-white/[0.14]">
                                        {PRACTICE.map((p, i) => (
                                            <div key={p.t} className={`px-5 py-5 ${i > 0 ? 'border-t border-white/[0.14]' : ''}`}>
                                                <h3 className="text-[15px] font-semibold tracking-[-0.01em] text-white/90">{p.t}</h3>
                                                <p className="mt-1.5 max-w-[520px] text-[13.5px] leading-[1.6] text-white/55">{p.b}</p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <div className="flex items-center justify-center">
                                    <MediaSlot
                                        dark
                                        kind="Photo"
                                        label="A drawer count sheet, a bank statement and a laptop on one table"
                                        note="Overhead, daylight, no faces. The paperwork and the screen showing the same day."
                                        ratio="3 / 4"
                                        className="!rounded-[24px] max-w-[320px]"
                                    />
                                </div>
                            </div>
                        </div>
                    </Reveal>
                </Container>
            </Section>

            {/* ── What we are not ────────────────────────────────────────── */}
            <Section tone="paper2" pad="lg">
                <Container>
                    <Reveal>
                        <SectionHead
                            title="What we are not."
                            lead="The fastest way to understand a back office layer is to know where it stops. These four boundaries are on purpose and they are not moving."
                            maxWidth="max-w-[520px]"
                        />
                    </Reveal>
                    <Reveal delay={80}>
                        <div className="mt-12 grid grid-cols-1 overflow-hidden rounded-[24px] border border-[rgba(10,10,10,0.10)] bg-white sm:grid-cols-2">
                            {NOT.map((x, i) => (
                                <div
                                    key={x.t}
                                    className={`flex flex-col gap-3 border-[rgba(10,10,10,0.08)] p-7 ${i > 0 ? 'border-t' : ''} ${i % 2 === 1 ? 'sm:border-l' : ''} ${i < 2 ? 'sm:border-t-0' : 'sm:border-t'}`}
                                >
                                    <span className="text-[13px] font-medium tabular-nums text-[#A3A3A3]">{String(i + 1).padStart(2, '0')}</span>
                                    <div className="text-[17px] font-semibold tracking-[-0.015em] text-[#0A0A0A]">{x.t}</div>
                                    <Body>{x.b}</Body>
                                </div>
                            ))}
                        </div>
                    </Reveal>
                </Container>
            </Section>

            {/* ── The company in brief ───────────────────────────────────── */}
            <Section pad="lg">
                <Container>
                    <div className="grid grid-cols-1 gap-10 lg:grid-cols-[340px_1fr] lg:gap-16">
                        <Reveal>
                            <div className="flex flex-col gap-4 lg:sticky lg:top-28">
                                <Heading>The company in brief.</Heading>
                                <Lead>One product, priced in public, running on infrastructure you can name. Anything we cannot state plainly here is not something we would claim on a call either.</Lead>
                                <div className="mt-2 flex flex-wrap gap-2">
                                    <Link to="/pricing" className={BTN_PRIMARY}>See pricing<Icon name="arrow" className="h-4 w-4" /></Link>
                                    <Link to="/security" className={BTN_SECONDARY}>How it is built</Link>
                                </div>
                            </div>
                        </Reveal>
                        <Reveal delay={80}>
                            <SpecList rows={BRIEF} />
                        </Reveal>
                    </div>
                </Container>
            </Section>

            {/* ── Closing CTA ────────────────────────────────────────────── */}
            <CTASection
                eyebrow="Talk to us"
                title="Bring a day we have never seen."
                body="The quickest way to judge this is to walk one of your own trading days through it, from the drawer count to the bank line, and see what comes out the other end."
                primary={{ label: 'Book a walkthrough', to: '/contact' }}
                secondary={{ label: 'See the platform', to: '/features' }}
            />
        </MarketingShell>
    );
}
